import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';

import { useGetCollaborationWorkLists } from '~/data/collaboration';
import { TabModules, useModulesConfig } from '~/data/company';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { Modules } from '~/data/modules';
import {
  TrainingStatusCodes,
  replaceTemplateIdInNotificationContent,
  replaceTrainingCodeInNotificationContent,
  replaceTrainingStatusCodeInNotificationsContent,
} from '~/hooks/useNotificationList/data';
import {
  deleteAll,
  deleteNotification,
  markAllAsRead,
  readNotification,
  unreadNotification,
} from '~/legacy-components/LegacyNotificationList/useLegacyNotifications';
import { EmployeeModuleTabConfigKey_People } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/PeopleModule/data';
import { NotificationTopic, People } from '~/services/Http';
import {
  GetNotificationsProps,
  useGetNotifications,
} from '~/services/Http/hooks';

export const revalidateNotificationsInterval = 60 * 1000;

export interface UseGetNotificationListOutput {
  notifications?: People.INotificationDto[];
  loading: boolean;
  failedToLoad: boolean;
  triggerMutation: (data: People.INotificationDto[]) => void;
  onMarkAsUnread: (id: number) => void;
  onMarkAsRead: (id: number) => void;
  onDeleteNotification: (id: number) => void;
  onMarkAllAsRead: () => void;
  onDeleteAll: () => void;
}

const useNotificationsProcessing = (
  props: {
    notifications: People.INotificationDto[];
    lang?: string;
  },
  swrConfig?: SWRConfiguration,
): People.INotificationDto[] => {
  const { notifications, lang } = props;

  const { isModuleEnabled, isModuleTabEnabled } = useModulesConfig();

  const { t } = useTranslation();
  const translations = useMemo(() => {
    const unknown = t('laUnknown');

    const trainingStatusTranslations: { [key in TrainingStatusCodes]: string } =
      {
        [TrainingStatusCodes.APPROVED]: t('laApproved'),
        [TrainingStatusCodes.REJECTED]: t('laRejected'),
        [TrainingStatusCodes.WAITING]: t('laWaitApproval'),
        [TrainingStatusCodes.CANCELLED]: t('label_cancelled'),
        [TrainingStatusCodes.COMPLETED]: t('laCompleted'),
        [TrainingStatusCodes.PARTICIPATED_PARTLY]: t('label_completed_partly'),
        [TrainingStatusCodes.DID_NOT_SHOW_UP]: t('label_did_not_show_up'),
        [TrainingStatusCodes.DID_NOT_COMPLETE]: t('label_did_not_complete'),
        [TrainingStatusCodes.PENDING]: t('label_PENDING'),
        [TrainingStatusCodes.UNKNOWN]: unknown,
      };

    return {
      trainingStatusTranslations,
      other: {
        unknown,
      },
    };
  }, [t]);

  const { getEnums } = useAllEnums();
  const { trainingEnums } = useMemo(() => {
    return {
      trainingEnums: {
        internal: getEnums(EnumListKey.INTERNAL_TRAINING_TYPE),
        external: getEnums(EnumListKey.EXTERNAL_TRAINING_TYPE),
      },
    };
  }, [getEnums]);

  const isCollaborationWorklistsEnabled =
    isModuleEnabled(Modules.COLLABORATION) &&
    isModuleTabEnabled(
      TabModules.PEOPLE,
      EmployeeModuleTabConfigKey_People.Collaboration,
    );

  const { data: worklistTemplates } = useGetCollaborationWorkLists(
    {
      language: lang,
      includeAll: true,
    },
    swrConfig,
    !isCollaborationWorklistsEnabled,
  );

  return useMemo(() => {
    return notifications.map((notification) => {
      const {
        content,
        topic: { code },
      } = notification;

      switch (code) {
        case NotificationTopic.WORKLIST_CREATED:
        case NotificationTopic.WORKLIST_COMPLETED:
          return {
            ...notification,
            content: replaceTemplateIdInNotificationContent(
              content,
              worklistTemplates,
              translations.other.unknown,
            ),
          };
        case NotificationTopic.TRAINING_APPROVAL_STATUS_INT_PARTICIPANT:
          return {
            ...notification,
            content: replaceTrainingCodeInNotificationContent(
              content,
              trainingEnums.internal,
              trainingEnums.external,
            ),
          };
        case NotificationTopic.TRAINING_PARTICIPANT_REG_STATUS:
        case NotificationTopic.TRAINING_PARTICIPANT_COMPLETION_STATUS:
          const approvalStatuses = [
            TrainingStatusCodes.APPROVED,
            TrainingStatusCodes.REJECTED,
            TrainingStatusCodes.WAITING,
            TrainingStatusCodes.CANCELLED,
          ];
          const completionStatuses = [
            TrainingStatusCodes.PENDING,
            TrainingStatusCodes.COMPLETED,
            TrainingStatusCodes.PARTICIPATED_PARTLY,
            TrainingStatusCodes.DID_NOT_SHOW_UP,
            TrainingStatusCodes.DID_NOT_COMPLETE,
          ];

          const statusCodes =
            code === NotificationTopic.TRAINING_PARTICIPANT_REG_STATUS
              ? approvalStatuses
              : completionStatuses;

          return {
            ...notification,
            content: replaceTrainingStatusCodeInNotificationsContent(
              content,
              statusCodes,
              translations.trainingStatusTranslations,
            ),
          };
        default:
          return notification;
      }
    });
  }, [notifications, worklistTemplates, trainingEnums, translations]);
};

export const useNotificationList = (
  props?: GetNotificationsProps,
  swrConfig?: SWRConfiguration<People.INotificationDto[]>,
): UseGetNotificationListOutput => {
  const config = useMemo(
    () => ({ refreshInterval: revalidateNotificationsInterval, ...swrConfig }),
    [swrConfig],
  );
  const {
    data: unprocessedNotifications,
    loading,
    failedToLoad,
    mutate,
  } = useGetNotifications(props, config);

  const notifications = useNotificationsProcessing(
    {
      notifications: unprocessedNotifications,
      lang: props?.lang,
    },
    config,
  );

  const triggerMutation = useCallback(
    (data: People.INotificationDto[]) => {
      mutate(data, false);
    },
    [mutate],
  );

  // Single notification actions
  const handleUpdateNotificationsReadState = (
    id: string | number,
    isRead: boolean,
  ) => {
    if (!notifications) {
      return triggerMutation([]);
    }

    const updatedNotifications = notifications.map((n) => {
      if (n.id !== id) {
        return n;
      }

      return { ...n, isRead };
    });

    triggerMutation(updatedNotifications);
  };

  const onMarkAsUnread = (id: number) => {
    unreadNotification(id).then(() =>
      handleUpdateNotificationsReadState(id, false),
    );
  };

  const onMarkAsRead = (id: number) => {
    readNotification(id).then(() =>
      handleUpdateNotificationsReadState(id, true),
    );
  };

  const onDeleteNotification = (id: number) => {
    deleteNotification(id).then(() => {
      triggerMutation(notifications?.filter((n) => n.id !== id) || []);
    });
  };

  // All notifications actions

  const onMarkAllAsRead = () => {
    markAllAsRead().then(() => {
      triggerMutation(
        notifications ? notifications.map((n) => ({ ...n, isRead: true })) : [],
      );
    });
  };

  const onDeleteAll = () => {
    deleteAll().then(() => {
      triggerMutation([]);
    });
  };

  return {
    notifications,
    loading,
    failedToLoad,
    triggerMutation,
    onMarkAsUnread,
    onMarkAsRead,
    onDeleteNotification,
    onMarkAllAsRead,
    onDeleteAll,
  };
};
